import { captureException } from '@sentry/node';
import { checkUsedPromoCode } from '@utils/PromoCode.utils';
import { getSessionIdCookie } from '@utils/Session.utils';
import { buildBrandIdRedirect, getInitalJourneyType } from '@utils/url.utils';
import { batch } from 'react-redux';
import CarDetailsDuck from '../../redux/carDetails/carDetails.duck';
import {
    B2B_URL,
    B2C_URL,
    C2S_WIDGET_QUERY_PARAMETER,
    IS_B2B,
    LEASYS_PROVIDER_TYPE,
    SESSION_COOKIE_NAME,
} from '../../constants/main';
import { IStockCar, IConfigurableCar } from '../../interfaces/Car';
import { MySavedDealJourneyType } from '../../redux/user/user.duck.interface';
import { CASH, FINANCE, DealService, dealerApi, CarJourneyType } from '../../services';
import { handleFinanceQuoteForDeal, handleLeasysQuoteForDeal, updateGeneralContext } from './common';
import { ContextWithStore } from './@types';
import DealerDuck from '../../redux/dealer/dealer.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import FilterDuck from 'src/redux/filters/filter.duck';
import GlobalDuck from 'src/redux/global/global.duck';
import routes from 'src/constants/routes';
import FinanceWidgetDuck from 'src/redux/financeWidget/financeWidget.duck';
import { validateDealerId } from '@utils/Deal.utils';
import UserDuck from 'src/redux/user/user.duck';
import ConfiguratorDuck from '../../redux/configurator/configurator.duck';
import { BRANDID_CLIENT_ID, BRANDID_LOGIN_URL, BRANDID_RETURN_URL } from '../../constants/myAccount';

export const getInitialPropsBasicDeal = async (
    ctx: ContextWithStore,
    isBasket: boolean = false,
    isDealerPage: boolean = false
) => {
    const { store, query, req, featureContext, res } = await updateGeneralContext(ctx);
    try {
        let token = req?.cookies?.[SESSION_COOKIE_NAME] ?? getSessionIdCookie();
        const isUserLoggedInAsDealer = UserDuck.isAgentLogged(store.getState());

        const isC2SEnabledForDealActivation = featureContext.FEATURE_SWITCH_IS_C2S_ENABLED_FOR_DEAL_ACTIVATION;

        if (query?.dealId && (!isC2SEnabledForDealActivation || query[C2S_WIDGET_QUERY_PARAMETER])) {
            let activateRes = null;

            try {
                activateRes = await DealService.activateDeal(
                    query.dealId,
                    token,
                    false,
                    String(query[C2S_WIDGET_QUERY_PARAMETER]),
                    true
                );
            } finally {
                const cookieHeader = activateRes?.headers?.['set-cookie'];
                token = activateRes?.headers?.['x-auth-token'] ?? token;
                if (cookieHeader && res?.setHeader) {
                    res.setHeader('set-cookie', cookieHeader);
                }
            }
        }

        await checkUsedPromoCode(store.dispatch, isUserLoggedInAsDealer);

        let currentDeal = CarDetailsDuck.getCurrentDeal<IStockCar | IConfigurableCar>(store.getState());
        let dealer = DealerDuck.getOwnState(store.getState()).dealer;

        if (!currentDeal) {
            currentDeal = (await DealService.currentDeal<IConfigurableCar | IStockCar>(token, true)).data;
        }

        let carDetails = currentDeal.fullProductConfiguration;
        const carJourney = getInitalJourneyType(carDetails);

        // APP-24414
        const isDealerChoiceEnabled = featureContext.FEATURE_SWITCH_IS_DEALER_CHOICE_ENABLED;
        const isLeasysEnabled = featureContext.FEATURE_SWITCH_IS_LEASYS_ENABLED;

        const isDealerSelected =
            carDetails.dealers?.[0] || !!dealer?.externalId || !!currentDeal.dealerId || !!dealer.city;

        let shouldHandleFinanceQuote = true;

        if (isDealerPage && isDealerChoiceEnabled && isDealerSelected) {
            try {
                const { data } = await DealService.deleteDealDealer(token);

                if (data.deal) currentDeal = data.deal;
                carDetails = currentDeal.fullProductConfiguration;

                if (data.financeQuote) {
                    store.dispatch(FinanceWidgetDuck.setFinanceQuote(data.financeQuote));
                    shouldHandleFinanceQuote = false;
                }
            } catch (error) {
                // noop
            }
        }

        if (isBasket && isDealerChoiceEnabled && !isDealerSelected && carJourney !== CarJourneyType.STOCK) {
            if (res) {
                res.writeHead(307, { Location: routes.DEALER });
                res.end();
                return { query, namespacesRequired: ['common'], statusCode: 307, featureContext, res };
            } else {
                window.location.href = window.location.origin + routes.DEALER;
            }
        }

        if (validateDealerId(currentDeal.dealerId) && !(carDetails.dealers?.[0] || !!dealer?.externalId)) {
            dealer = (await dealerApi.getDealer(currentDeal.dealerId)).data;
            store.dispatch(DealerDuck.setDealer(dealer));
        }

        if (shouldHandleFinanceQuote) {
            await handleFinanceQuoteForDeal(store, currentDeal, isLeasysEnabled);
            await handleLeasysQuoteForDeal(store, currentDeal, isLeasysEnabled);
        }

        batch(() => {
            store.dispatch(UIDuck.setIsDealSaved(Boolean(query?.saved)));
            store.dispatch(CarDetailsDuck.setCurrentDeal<IStockCar | IConfigurableCar>(currentDeal));
            store.dispatch(CarDetailsDuck.setCurrentCarDetails(carDetails));
            store.dispatch(GlobalDuck.setCarJourney(carJourney));
            store.dispatch(UIDuck.closeCharacteristics());
            store.dispatch(ConfiguratorDuck.setOptions([]));
            store.dispatch(ConfiguratorDuck.setAvailableOptions([]));
            store.dispatch(UIDuck.setLoading(5));
            store.dispatch(
                FilterDuck.changeBudgetType(
                    currentDeal.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH ? CASH : FINANCE
                )
            );
        });

        store.dispatch(UIDuck.setLoading(-5));
    } catch (e: any) {
        if (e.response?.status === 401 && e.response?.data?.userNeedLoginFirst) {
            const brandIdState = {
                redirectTo: `${IS_B2B ? B2B_URL : B2C_URL}/basket?dealId=${query?.dealId}&c2sFolder=${
                    query[C2S_WIDGET_QUERY_PARAMETER]
                }`,
            };

            const brandIdStateJson: string = JSON.stringify(brandIdState);

            const brandIdUrl = BRANDID_LOGIN_URL.replace('{clientId}', BRANDID_CLIENT_ID).replace(
                '{redirectURL}',
                BRANDID_RETURN_URL
            );
            const redirectUrl = buildBrandIdRedirect(brandIdUrl, brandIdStateJson, {
                isLogin: true,
            });

            res.writeHead(307, { Location: redirectUrl });
            res.end();
            return { query, namespacesRequired: ['common'], statusCode: 307, featureContext, res };
        }
        captureException(e);
        console.error(e);
        store.dispatch(UIDuck.setLoading(-5));
    }
    return { query, namespacesRequired: ['common'], statusCode: 201, featureContext };
};
